import { CanceledError, type InternalAxiosRequestConfig } from 'axios';
import { focusManager, onlineManager } from '@tanstack/react-query';
import { useSessionStore } from '../stores/sessionStore';

/**
 * The one gate every background request checks once the sign-in has ended.
 *
 * When a login expires, api/client.ts tries one renewal and then shows the
 * Session Expired dialog. Until the user signs in again nothing can succeed,
 * yet every poller kept going (one tab sent ~754 401s over 26 hours). Now:
 *
 * - the axios request interceptors refuse to send a user-session request
 *   (`SessionExpiredError`, never on the wire);
 * - React Query stops refetchInterval / focus / reconnect polling
 *   (`installQueryPollingGate`, set up once in App.tsx);
 * - plain timers check `canPollNow()` on each tick.
 *
 * Blocked means the Session Expired dialog is up (sessionStore.isSessionExpired),
 * not merely that a proactive renewal found the refresh token dead: the page may
 * still hold a working access token then, and its requests must go out.
 *
 * Tablets, bedside TVs and Selective Touch devices never use the user's sign-in
 * (device tokens / tablet PIN sessions) and are not affected.
 */

/** True once the sign-in has ended: API requests wait for the user to sign in again. */
export function isApiBlocked(): boolean {
  return useSessionStore.getState().isSessionExpired;
}

/** Reactive form of `!isApiBlocked()` for effects that start a timer. */
export function usePollingAllowed(): boolean {
  return !useSessionStore((s) => s.isSessionExpired);
}

/**
 * For a plain timer's tick: false once signed out, and while the tab is hidden
 * unless `whileHidden` (an edit-lock heartbeat must keep beating in a background
 * tab or the user loses the lock).
 */
export function canPollNow(options: { whileHidden?: boolean } = {}): boolean {
  if (isApiBlocked()) return false;
  if (!options.whileHidden && typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
  return true;
}

/**
 * A request the gate refused to send. A CanceledError (code ERR_CANCELED,
 * `axios.isCancel` true) so code that already ignores cancelled requests
 * ignores this one too, and the "API down" and failed-call tracking skip it.
 */
export class SessionExpiredError extends CanceledError<unknown> {
  readonly sessionExpired = true;

  constructor(config?: InternalAxiosRequestConfig) {
    // axios's typings give CanceledError AxiosError's (message, code, config)
    // signature; at runtime it is (message, config, request).
    super('Signed out - sign in again to continue', config as never);
  }
}

/** True for a request the gate refused to send. */
export function isSessionExpiredError(err: unknown): boolean {
  return err instanceof SessionExpiredError
    || (typeof err === 'object' && err !== null && (err as { sessionExpired?: unknown }).sessionExpired === true);
}

/** True when a request failed because the user is signed out: a 401, or refused by the gate. */
export function isSignedOutError(err: unknown): boolean {
  if (isSessionExpiredError(err)) return true;
  return (err as { response?: { status?: number } } | null)?.response?.status === 401;
}

/**
 * True for an error that only says the user is signed out: refused by the
 * gate, or a 401 once the Session Expired dialog is up. The dialog already says
 * so; the global "Operation failed" toast skips these.
 */
export function isSignedOutNoise(err: unknown): boolean {
  return isSessionExpiredError(err) || (isSignedOutError(err) && isApiBlocked());
}

/**
 * Stops React Query's background polling while the session is expired, and
 * restores it if the session becomes valid again.
 *
 * - focusManager.setFocused(false): refetchInterval only fires while focused
 *   (no query here sets refetchIntervalInBackground), and window-focus refetches
 *   stop. The visibilitychange listener does not undo an explicit value.
 * - onlineManager.setOnline(false): reconnect refetches stop, and any fetch or
 *   mutation that still starts waits (paused) instead of failing with an error.
 *
 * Returns the unsubscribe.
 */
export function installQueryPollingGate(): () => void {
  let paused = false;
  const apply = (expired: boolean) => {
    if (expired === paused) return;
    paused = expired;
    if (expired) {
      focusManager.setFocused(false);
      onlineManager.setOnline(false);
    } else {
      focusManager.setFocused(undefined);
      onlineManager.setOnline(typeof navigator === 'undefined' || navigator.onLine !== false);
    }
  };
  apply(useSessionStore.getState().isSessionExpired);
  const unsubscribe = useSessionStore.subscribe((s) => apply(s.isSessionExpired));
  return () => {
    unsubscribe();
    apply(false);
  };
}
